import { CONST } from '../utils/constants';
import DateUtils from '../utils/dateUtils';
import {
  TrainerPaymentTypeEnum,
  getTrainerPaymentTypeText,
} from './trainerPayment';

export type TrainerHoursStatusEnum = 0 | 1;
export const TrainerHoursStatusEnum = {
  Open: 0 as TrainerHoursStatusEnum,
  PaidOut: 1 as TrainerHoursStatusEnum,
};

export interface TrainerHours {
  id: string;
  trainerId: string;
  trainerFullName?: string;
  trainerPaymentId?: string;
  courseId?: string;
  attendanceId?: string;
  date: string;
  timeFrom: string;
  timeTill: string;
  feeTotalEuro: number;
  costCenter: string;
  status: TrainerHoursStatusEnum;
  exported: boolean;
  description: string;
  creatorId?: string;
  creatorName?: string;
  createdDate: string;
  companyId: string;
  trainerPaymentType: TrainerPaymentTypeEnum;
  courseName?: string;
  departmentName?: string;
  hours: number;
  lastUpdatedByEmployeeName?: string;
  lastUpdatedDate?: string;
}

export interface TrainerHoursFeeSums {
  feeOpen: number;
  feePaidOut: number;
  feeLimit: number | null | undefined;
}

export interface Value {
  value: TrainerHours[];
  '@odata.context'?: string | undefined;
  '@odata.count'?: number | undefined;
}

function toAPI(data: Partial<TrainerHours>): any {
  return {
    Id: data?.id || CONST.emptyGuid,
    TrainerId: data?.trainerId || CONST.emptyGuid,
    TrainerPaymentId: data?.trainerPaymentId || null,
    CourseId: data?.courseId || null,
    AttendanceId: data?.attendanceId || null,
    Date: data?.date || DateUtils.getTodayDateAsIsoString(),
    TimeFrom: data?.timeFrom || '00:00',
    TimeTill: data?.timeTill || '00:00',
    FeeTotalEuro: data?.feeTotalEuro || 0,
    CostCenter: data?.costCenter || '',
    Status: data?.status ?? TrainerHoursStatusEnum.Open,
    Exported: data?.exported ?? false,
    Description: data?.description || '',
    LastUpdatedByEmployeeName: data?.lastUpdatedByEmployeeName || undefined,
    LastUpdatedDate: data?.lastUpdatedDate || undefined,
  };
}

function parse(
  data?: Partial<
    TrainerHours & { timeFromString: string; timeTillString: string }
  >
): TrainerHours {
  const type =
    typeof data?.trainerPaymentType === 'string'
      ? data.trainerPaymentType
      : getTrainerPaymentTypeText(
          data?.trainerPaymentType ?? TrainerPaymentTypeEnum.HourlyRate
        );
  const status =
    typeof data?.status === 'string'
      ? data.status
      : getTrainerHoursStatusText(data?.status ?? TrainerHoursStatusEnum.Open);

  return {
    id: data?.id || '',
    trainerId: data?.trainerId || '',
    trainerFullName: data?.trainerFullName || undefined,
    trainerPaymentId: data?.trainerPaymentId || undefined,
    courseId: data?.courseId || undefined,
    attendanceId: data?.attendanceId || undefined,
    date: data?.date || DateUtils.getTodayDateAsIsoString(),
    timeFrom: data?.timeFromString?.substring(0, 5) || '00:00',
    timeTill: data?.timeTillString?.substring(0, 5) || '00:00',
    feeTotalEuro: data?.feeTotalEuro || 0,
    costCenter: data?.costCenter || '',
    status:
      TrainerHoursStatusEnum[status as keyof typeof TrainerHoursStatusEnum],
    exported: data?.exported ?? false,
    description: data?.description || '',
    creatorId: data?.creatorId || undefined,
    creatorName: data?.creatorName || undefined,
    createdDate:
      data?.createdDate || DateUtils.getCurrentLocalDateAsIsoString(),
    companyId: data?.companyId || '',
    trainerPaymentType:
      TrainerPaymentTypeEnum[type as keyof typeof TrainerPaymentTypeEnum],
    courseName: data?.courseName || undefined,
    departmentName: data?.departmentName || undefined,
    hours: data?.hours || 0,
    lastUpdatedByEmployeeName: data?.lastUpdatedByEmployeeName || undefined,
    lastUpdatedDate: data?.lastUpdatedDate || undefined,
  };
}

export default {
  parse,
  toAPI,
};

export function getTrainerHoursStatusText(
  type: TrainerHoursStatusEnum
): string {
  return Object.keys(TrainerHoursStatusEnum)[
    Object.values(TrainerHoursStatusEnum).findIndex((entry) => entry == type)
  ];
}
